// @react
import React, { ReactElement, Fragment, useRef, useState } from 'react'
// @typings
import { ContactFormValues } from 'modules/common/forms/types/contact'
// @libraries
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormMethods,
} from 'react-hook-form'
// @data
import { ATD_country_options } from 'modules/common/forms/fields/_config/addresss'
// @components
import Grid from '@bit/atd.web.grid'
import Divider from '@bit/atd.web.divider'
import InputSectionLabel from '@bit/atd.web.forms.input-section-label'
import InputRadioGroup from '@bit/atd.web.forms.input-radio-group'
import Button from '@bit/atd.web.button'
import Select from '@bit/atd.web.select'
// @module components
import AddressAutoComplete from 'modules/common/forms/fields/contact/AddressAutoComplete'
import AddressFieldsInArray from 'modules/common/forms/fields/contact/Address/AddressArray'
import RemoveModal from 'modules/common/components/RemoveModal'

const MAX = 10

type PropsType = {
  formMethods: UseFormMethods<ContactFormValues>
  control: Control<ContactFormValues>
  errors: FieldErrors<ContactFormValues>
  onRemove: (id: number) => Promise<boolean>
}

const AddressArrayFields = ({
  formMethods,
  control,
  errors,
  onRemove,
}: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalIndex, setModalIndex] = useState<number | undefined>()
  const [modalAddressId, setModalAddressId] = useState<number | undefined>()

  /****************************************
   * HOOKS
   *****************************************/

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'addresses',
  })

  /****************************************
   * REFS
   *****************************************/

  const typeAddressRef = useRef<HTMLInputElement | null>(null)
  const countryRef = useRef<HTMLInputElement | null>(null)

  /****************************************
   * RENDER
   *****************************************/

  return (
    <>
      {fields.map((item, index) => (
        <Fragment key={item.id}>
          {/* Divider - Shown after initial index */}
          {index > 0 && (
            <Grid xs={12} item>
              <Divider width="100%" color="#DBD9D6" height="1px" />
            </Grid>
          )}

          {/* Legend - Header */}
          <Grid xs={12} item>
            {index === 0 ? (
              <InputSectionLabel component={'legend'}>
                Primary Address
              </InputSectionLabel>
            ) : (
              <InputSectionLabel component={'legend'}>
                {`Address ${index}`}
              </InputSectionLabel>
            )}
          </Grid>

          {/* Places Auto Complete */}
          <AddressAutoComplete index={index} formMethods={formMethods} />

          {/* Address ID */}
          <Controller
            control={control}
            name={`addresses[${index}].addressId`}
            defaultValue={item.addressId}
            render={(props) => <input hidden type="text" {...props} />}
          />

          {/* IsPrimary */}
          <Controller
            control={control}
            name={`addresses[${index}].isPrimary`}
            defaultValue={item.isPrimary}
            render={(props) => <input hidden type="text" {...props} />}
          />

          {/* Type of Address */}
          <Grid xs={12} item>
            <Controller
              control={control}
              name={`addresses[${index}].addressType`}
              onFocus={() => typeAddressRef?.current?.focus()}
              defaultValue={item.addressType}
              render={({ onChange, onBlur, value, name }) => (
                <InputRadioGroup
                  name={name}
                  disabled={!!item.addressId}
                  legend="What type of address is this?"
                  layout="column"
                  labelPlacement="right"
                  size="small"
                  value={value as string}
                  options={[
                    {
                      label: 'Personal',
                      value: 'HOME',
                    },
                    {
                      label: 'Business',
                      value: 'BUSINESS',
                    },
                  ]}
                  onBlur={onBlur}
                  onChange={onChange}
                  inputRef={typeAddressRef}
                  error={!!errors?.addresses?.[index]?.addressType}
                  errorText={errors?.addresses?.[index]?.addressType?.message}
                />
              )}
            />
          </Grid>

          {/* Country */}
          <Grid xs={12} sm={6} item>
            <Controller
              control={control}
              name={`addresses[${index}].countryCode`}
              onFocus={() => countryRef?.current?.focus()}
              defaultValue={item.countryCode}
              render={({ onChange, onBlur, value, name }) => (
                <Select
                  native
                  required
                  name={name}
                  label="Country"
                  placeholder="Choose your country."
                  options={ATD_country_options}
                  value={value as string}
                  onBlur={onBlur}
                  onChange={onChange}
                  inputRef={countryRef}
                  error={!!errors?.addresses?.[index]?.countryCode}
                  errorText={errors?.addresses?.[index]?.countryCode?.message}
                />
              )}
            />
          </Grid>

          <AddressFieldsInArray
            item={item}
            index={index}
            control={control}
            formMethods={formMethods}
            errors={errors}
          />

          {/* Button - Remove Address */}
          {index > 0 && (
            <Grid xs={12} item>
              <Button
                onClick={() => {
                  setModalOpen(true)
                  setModalIndex(index)
                  setModalAddressId(item.addressId)
                }}
                size="medium"
                variant="secondary"
              >
                Remove Address
              </Button>
            </Grid>
          )}
        </Fragment>
      ))}

      {/* Add More */}
      {fields.length < MAX && (
        <Grid xs={12} item>
          <Button
            onClick={() =>
              append({
                addressId: '',
                isPrimary: false,
                addressType: '',
                countryCode: '',
                address1: '',
                address2: '',
                address3: '',
                city: '',
                postalCode: '',
                state: '',
              })
            }
            size="medium"
          >
            Add New Address
          </Button>
        </Grid>
      )}
      {modalIndex !== undefined && (
        <RemoveModal
          isOpen={modalOpen}
          setIsOpen={setModalOpen}
          submitHandler={async () => {
            if (!modalAddressId) return remove(modalIndex)
            const res = await onRemove(modalIndex)
            res && remove(modalIndex)
          }}
        />
      )}
    </>
  )
}

export default AddressArrayFields
